/**
 * @note we took this from fastdom, but their code did not integrate well in TS, so we
 * refactored to a simpler version
 *
 * FastDom
 *
 * Eliminates layout thrashing
 * by batching DOM read/write
 * interactions.
 *
 * @author Wilson Page <wilsonpage@me.com>
 * @author Kornel Lesinski <kornel.lesinski@ft.com>
 */

export type t_task = () => void;

/**
 * Normalized rAF
 *
 * @type {Function}
 */
const raf =
  window.requestAnimationFrame ||
  window.webkitRequestAnimationFrame ||
  window['mozRequestAnimationFrame'] ||
  window['msRequestAnimationFrame'] ||
  function (cb) {
    return setTimeout(cb, 16);
  };

/**
 * Schedules a new read/write
 * batch if one isn't pending.
 *
 * @private
 */
function scheduleFlush(domQueue: DomQueue) {
  if (!domQueue.scheduled) {
    domQueue.scheduled = true;
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    domQueue.raf(flush.bind(null, domQueue));
  }
}

/**
 * We run this inside a try catch
 * so that if any jobs error, we
 * are able to recover and continue
 * to flush the batch until it's empty.
 *
 * @private
 */
function runTasks(tasks: t_task[]) {
  let task;
  while ((task = tasks.shift())) {
    task();
  }
}

/**
 * Runs queued `read` and `write` tasks.
 *
 * Errors are caught and thrown by default.
 * If a `.catch` function has been defined
 * it is called instead.
 *
 * @private
 */
function flush(domQueue: DomQueue) {
  const writes = domQueue.writes;
  const reads = domQueue.reads;
  let error;

  try {
    runTasks(reads);
    runTasks(writes);
  } catch (e) {
    error = e;
  }

  domQueue.scheduled = false;

  // If the batch errored we may still have tasks queued
  if (reads.length || writes.length) scheduleFlush(domQueue);

  if (error) {
    throw error;
  }
}

/**
 * Remove an item from an Array.
 *
 * @param  {Array} array
 * @param  {*} item
 * @return {Boolean}
 */
function remove(array: t_task[], item: t_task) {
  const index = array.indexOf(item);
  return !!~index && !!array.splice(index, 1);
}

/**
 * Initialize a `FastDom`.
 *
 * @constructor
 */
class DomQueue {
  writes: t_task[] = [];
  reads: t_task[] = [];
  scheduled = false;

  raf = raf.bind(window);

  /**
   * Adds a job to the read batch and
   * schedules a new frame if need be.
   *
   * @param  {Function} fn
   * @param  {Object} ctx the context to be bound to `fn` (optional).
   * @public
   */
  measure(fn: t_task): t_task {
    const task = fn;
    this.reads.push(task);
    scheduleFlush(this);
    return task;
  }

  /**
   * Adds a job to the
   * write batch and schedules
   * a new frame if need be.
   *
   * @param  {Function} fn
   * @param  {Object} ctx the context to be bound to `fn` (optional).
   * @public
   */
  mutate(fn: t_task): t_task {
    const task = fn;
    this.writes.push(task);
    scheduleFlush(this);
    return task;
  }

  /**
   * Clears a scheduled 'read' or 'write' task.
   *
   * @param {Object} task
   * @return {Boolean} success
   * @public
   */
  clear(task: t_task) {
    return remove(this.reads, task) || remove(this.writes, task);
  }
}

export const domQueue = new DomQueue();
